import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { FinanceService, ReferenceEntry, ResourceInfoQuery } from '../../finance.service';

export type FilterByModalResult = Omit<ResourceInfoQuery, 'page' | 'size' | 'search'>;

export interface FilterByModalData {
  initial: FilterByModalResult;
}

type FilterKind = 'multiSelect' | 'text' | 'boolean';

interface FilterSlot {
  /** Matches the corresponding field on ResourceInfoQuery. */
  key: keyof FilterByModalResult;
  label: string;
  kind: FilterKind;
  /**
   * For {@code multiSelect} slots backed by a finance reference table,
   * the slug to fetch via {@code GET /finance/reference/{slug}}.
   * When omitted, the dropdown stays empty (used today for projectIds
   * until the Project Catalog list endpoint ships).
   */
  referenceSlug?: string;
}

/**
 * Filter-by modal for the Resource Info table. Slot-driven so adding a new
 * filter is a one-line change to {@link #slots} — the template + load logic
 * pick up the new control automatically.
 *
 * <p>On open, every {@code multiSelect} slot with a {@code referenceSlug}
 * fires its lookup in parallel via forkJoin. Apply emits a
 * {@link FilterByModalResult} carrying only the populated filters; empty
 * arrays and blank strings are stripped so the parent doesn't end up sending
 * noise to the BE.
 */
@Component({
  selector: 'app-filter-by-modal',
  templateUrl: './filter-by-modal.component.html',
  styleUrl: './filter-by-modal.component.scss',
})
export class FilterByModalComponent implements OnInit {
  /**
   * Per the Figma the modal is a 3-column × 8-row grid laid out
   * column-major (top-to-bottom, then left-to-right). All controls are
   * multi-select dropdowns. Dropdowns whose backing list isn't seeded yet
   * (name-typed columns waiting on a distinct-values endpoint) render with
   * an empty option set + a "No options available" placeholder.
   *
   * Filters intentionally NOT in this modal (per Figma): Hiring Status,
   * Region, Rate Card Match, Review Status, Needs Review, New Hires Only.
   * Their BE @RequestParams stay so the inline header filters or future
   * extensions can use them — they just aren't surfaced here.
   */
  readonly slots: FilterSlot[] = [
    // ----- Column 1 (left) -----
    { key: 'supportingLobs',     label: 'Supporting LOB',       kind: 'multiSelect', referenceSlug: 'supporting-lob' },
    { key: 'dntIndiaLeaders',    label: 'D&T India Leader',     kind: 'multiSelect' /* no reference yet */ },
    { key: 'projectIds',         label: 'Project Name & ID',    kind: 'multiSelect' /* awaiting Project Catalog endpoint */ },
    { key: 'allocationBuckets',  label: 'Allocation',           kind: 'multiSelect' /* options come from buildAllocationOptions() */ },
    { key: 'capexOpex',          label: 'CAPEX/OPEX',           kind: 'multiSelect', referenceSlug: 'capex-opex' },
    { key: 'poTypes',            label: 'PO Type',              kind: 'multiSelect', referenceSlug: 'po-type' },
    { key: 'locationTypes',      label: 'Location Type',        kind: 'multiSelect', referenceSlug: 'location-type' },
    { key: 'poiTypes',           label: 'POI Type',             kind: 'multiSelect', referenceSlug: 'poi-type' },
    // ----- Column 2 (middle) -----
    { key: 'employers',          label: 'Employer',             kind: 'multiSelect', referenceSlug: 'employer' },
    { key: 'tierModels',         label: 'Tier Model',           kind: 'multiSelect', referenceSlug: 'tier-model' },
    { key: 'activities',         label: 'Activity',             kind: 'multiSelect' /* derived from capex_opex — see ngOnInit */ },
    { key: 'categories',         label: 'Category',             kind: 'multiSelect' /* derived from capex_opex */ },
    { key: 'laptopIssued',       label: 'Laptop Issued',        kind: 'multiSelect', referenceSlug: 'laptop-issued' },
    { key: 'cities',             label: 'City',                 kind: 'multiSelect' /* distinct values, endpoint TBD */ },
    { key: 'businessSegments',   label: 'Business SEG',         kind: 'multiSelect', referenceSlug: 'business-segment' },
    { key: 'productSuites',      label: 'Product Suite',        kind: 'multiSelect', referenceSlug: 'product-suite' },
    // ----- Column 3 (right) -----
    { key: 'agileTeams',         label: 'Agile Team',           kind: 'multiSelect', referenceSlug: 'agile-team' },
    { key: 'reportingManager',   label: 'RM',                   kind: 'text' /* manual entry per Product */ },
    { key: 'l4Managers',         label: 'L4 MGR',               kind: 'multiSelect' /* distinct l4_name from Employee */ },
    { key: 'functionalManagers', label: 'Functional MGR',       kind: 'multiSelect' /* distinct names, endpoint TBD */ },
    { key: 'vps',                label: 'VP',                   kind: 'multiSelect' /* distinct names, endpoint TBD */ },
    { key: 'svps',               label: 'SVP',                  kind: 'multiSelect' /* distinct names, endpoint TBD */ },
    { key: 'invoiceApprovers',   label: 'D&T Invoice Approver', kind: 'multiSelect' /* distinct names, endpoint TBD */ },
    { key: 'invoiceLocations',   label: 'Invoice Location',     kind: 'multiSelect', referenceSlug: 'invoice-location' },
  ];

  loading = true;
  /** Reference entries keyed by slot key — populated by ngOnInit forkJoin. */
  options: Record<string, ReferenceEntry[]> = {};
  /** Currently-selected values keyed by slot key. Same shape as ResourceInfoQuery field types. */
  selections: Record<string, unknown> = {};

  constructor(
    private readonly dialogRef: MatDialogRef<FilterByModalComponent, FilterByModalResult>,
    @Inject(MAT_DIALOG_DATA) private readonly data: FilterByModalData,
    private readonly financeService: FinanceService,
  ) {
    // Seed selections from the parent's currently-applied filters.
    const initial = (data.initial ?? {}) as Record<string, unknown>;
    for (const slot of this.slots) {
      const v = initial[slot.key as string];
      if (v !== undefined && v !== null) {
        this.selections[slot.key as string] = v;
      } else if (slot.kind === 'multiSelect') {
        this.selections[slot.key as string] = [];
      } else if (slot.kind === 'text') {
        this.selections[slot.key as string] = '';
      } else if (slot.kind === 'boolean') {
        this.selections[slot.key as string] = false;
      }
    }
  }

  ngOnInit(): void {
    // Seed the slots whose options aren't backed by a reference table.
    this.options['allocationBuckets'] = this.buildAllocationOptions();
    this.options['activities'] = this.buildActivityOptions();
    this.options['categories'] = this.buildCategoryOptions();

    const refLoaders: Record<string, ReturnType<FinanceService['listReferenceEntries']>> = {};
    for (const slot of this.slots) {
      if (slot.kind === 'multiSelect' && slot.referenceSlug) {
        refLoaders[slot.key as string] = this.financeService
          .listReferenceEntries(slot.referenceSlug)
          .pipe(catchError(() => of([] as ReferenceEntry[])));
      }
    }

    // Project Name & ID — Catalog endpoint that already backs the Edit
    // Project picker. Map to ReferenceEntry shape so the existing
    // optionsFor() pipeline doesn't need a special-case branch.
    this.financeService
      .listFinanceProjects()
      .pipe(catchError(() => of([])))
      .subscribe((projects) => {
        this.options['projectIds'] = projects.map((p, idx) => ({
          code: String(p.id),
          label: this.formatProjectLabel(p),
          sortOrder: idx,
          isActive: true,
        }));
      });

    // Distinct project-side suggestions (SVP, VP, Functional MGR, D&T
    // Invoice Approver, Product Suite, Agile Team) come from the
    // autocomplete endpoint already loaded by the Edit / Add Project
    // form. One round-trip, fans out into 6 filter dropdowns.
    this.financeService
      .getProjectAutocompleteSuggestions()
      .pipe(catchError(() => of({
        productSuites: [], agileTeams: [], svps: [], vps: [],
        functionalManagers: [], invoiceApprovers: [],
      })))
      .subscribe((sug) => {
        this.options['productSuites']     = this.stringsToOptions(sug.productSuites);
        this.options['agileTeams']        = this.stringsToOptions(sug.agileTeams);
        this.options['svps']              = this.stringsToOptions(sug.svps);
        this.options['vps']               = this.stringsToOptions(sug.vps);
        this.options['functionalManagers']= this.stringsToOptions(sug.functionalManagers);
        this.options['invoiceApprovers']  = this.stringsToOptions(sug.invoiceApprovers);
      });

    // Distinct Employee-side values — L3 (D&T India Leader), L4 (L4
    // Manager), City. Reporting Manager is intentionally NOT fetched
    // here; its filter slot is a free-text input.
    this.financeService
      .getEmployeeDistinctValues()
      .pipe(catchError(() => of({ l3Names: [], l4Names: [], cities: [] })))
      .subscribe((dv) => {
        this.options['dntIndiaLeaders'] = this.stringsToOptions(dv.l3Names);
        this.options['l4Managers']      = this.stringsToOptions(dv.l4Names);
        this.options['cities']          = this.stringsToOptions(dv.cities);
      });

    if (Object.keys(refLoaders).length === 0) {
      this.loading = false;
      return;
    }
    forkJoin(refLoaders).subscribe({
      next: (loaded) => {
        for (const key of Object.keys(loaded)) {
          this.options[key] = loaded[key];
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  /** Helper: turn a flat string[] of distinct values into the
   *  ReferenceEntry shape the dropdown rendering expects. */
  private stringsToOptions(values: string[]): ReferenceEntry[] {
    return values
      .filter((v) => v != null && v.trim().length > 0)
      .map((v, idx) => ({ code: v, label: v, sortOrder: idx, isActive: true }));
  }

  /** "Project Name | ID-code" when both are present, otherwise whichever
   *  field is populated, with an ID-numbered fallback for unnamed rows. */
  private formatProjectLabel(p: { id: number; projectName?: string; projectIdCode?: string }): string {
    if (p.projectName && p.projectIdCode) return `${p.projectName} | ${p.projectIdCode}`;
    if (p.projectName) return p.projectName;
    if (p.projectIdCode) return p.projectIdCode;
    return `Project ${p.id}`;
  }

  /** 10%, 20%, … 100% as ReferenceEntry-shaped options. Value side stays numeric. */
  private buildAllocationOptions(): ReferenceEntry[] {
    return Array.from({ length: 10 }, (_, i) => {
      const pct = (i + 1) * 10;
      return {
        code: String(pct),
        label: `${pct}%`,
        sortOrder: pct,
        isActive: true,
      };
    });
  }

  /** Activity values are derived in the BE from capex_opex — known finite set. */
  private buildActivityOptions(): ReferenceEntry[] {
    return [
      { code: 'CAP_LABOR', label: 'CAP_LABOR', sortOrder: 10, isActive: true },
      { code: 'EXPENSE',   label: 'EXPENSE',   sortOrder: 20, isActive: true },
      { code: 'CAP_COST',  label: 'CAP_COST',  sortOrder: 30, isActive: true },
    ];
  }

  /** Category values are derived in the BE from capex_opex — known finite set. */
  private buildCategoryOptions(): ReferenceEntry[] {
    return [
      { code: 'OCSLT', label: 'OCSLT', sortOrder: 10, isActive: true },
      { code: 'OTHEX', label: 'OTHEX', sortOrder: 20, isActive: true },
    ];
  }

  optionsFor(slot: FilterSlot): ReferenceEntry[] {
    return this.options[slot.key as string] ?? [];
  }

  /** Per-slot inline search text — keyed by slot.key so each dropdown
   *  has its own search box that filters that dropdown's options only.
   *  Cleared when the dropdown panel closes so the next open starts
   *  fresh. */
  slotSearchText: Record<string, string> = {};

  /** Returns the options list filtered by the slot's current search
   *  text. Case-insensitive substring match on both label and code so
   *  operators can type either the human-readable name or the code. */
  filteredOptionsFor(slot: FilterSlot): ReferenceEntry[] {
    const all = this.optionsFor(slot);
    const q = (this.slotSearchText[slot.key as string] ?? '').trim().toLowerCase();
    if (!q) return all;
    return all.filter((o) =>
      (o.label ?? '').toLowerCase().includes(q)
      || (o.code ?? '').toLowerCase().includes(q),
    );
  }

  /** Reset the slot's search when its panel closes — without this the
   *  next open would still show the prior subset. */
  onSlotPanelOpenedChange(opened: boolean, slotKey: string): void {
    if (!opened) this.slotSearchText[slotKey] = '';
  }

  /** Drives the "N selected" / "All" trigger text in the template. */
  selectionCount(slot: FilterSlot): number {
    const v = this.selections[slot.key as string];
    return Array.isArray(v) ? v.length : 0;
  }

  apply(): void {
    const result: Record<string, unknown> = {};
    for (const slot of this.slots) {
      const v = this.selections[slot.key as string];
      if (slot.kind === 'multiSelect' && Array.isArray(v) && v.length > 0) {
        // allocationBuckets is typed as number[] on the BE; the dropdown
        // value side renders option codes as strings. Coerce here so the
        // wire payload matches the BE's expected element type.
        if (slot.key === 'allocationBuckets') {
          result[slot.key as string] = v.map(Number).filter((n) => !Number.isNaN(n));
        } else {
          result[slot.key as string] = [...v];
        }
      } else if (slot.kind === 'text' && typeof v === 'string' && v.trim()) {
        result[slot.key as string] = v.trim();
      } else if (slot.kind === 'boolean' && v === true) {
        result[slot.key as string] = true;
      }
    }
    this.dialogRef.close(result);
  }

  reset(): void {
    for (const slot of this.slots) {
      if (slot.kind === 'multiSelect') {
        this.selections[slot.key as string] = [];
      } else if (slot.kind === 'text') {
        this.selections[slot.key as string] = '';
      } else if (slot.kind === 'boolean') {
        this.selections[slot.key as string] = false;
      }
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
